import React, { useEffect, useState, useCallback } from "react";
import {
    ClassicEditor,
    Bold,
    Essentials,
    Italic,
    Mention,
    Paragraph,
    Undo,
    Image,
    ImageToolbar,
    ImageCaption,
    ImageStyle,
    ImageResize,
    ImageUpload,
    Base64UploadAdapter,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    EditorConfig,
    SourceEditing,
    Font,
    FontSize,
    FontColor,
    FontBackgroundColor,
    Link,
    List,
    BlockQuote,
    Alignment,
    EventInfo,
    PluginConstructor,
    Editor,
} from "ckeditor5";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";

interface RickTextEditorProps {
    initialData?: string;
    isInvalid?: boolean;
    initialLoading?: boolean;
    helperText?: string;
    onChange?: (data: string) => void;
    config?: object;
    allowImageUpload?: boolean;
}

const RichTextEditor: React.FC<RickTextEditorProps> = ({
    initialData = "",
    onChange,
    config,
    isInvalid,
    helperText,
    initialLoading = false,
    allowImageUpload = true,
}) => {
    const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
    const [loading, setLoading] = useState(initialLoading);

    useEffect(() => {
        const toolbarItems = [
            "sourceEditing",
            "bold",
            "italic",
            "link",
            "fontFamily",
            "fontSize",
            "fontColor",
            "fontBackgroundColor",
            "alignment",
            "bulletedList",
            "numberedList",
            "blockQuote",
            "undo",
            "redo",
            "insertTable",
        ];

        const plugins: Array<PluginConstructor<Editor> | string> = [
            Essentials,
            Bold,
            Italic,
            Paragraph,
            Undo,
            Mention,
            Font,
            FontSize,
            FontColor,
            FontBackgroundColor,
            Link,
            List,
            BlockQuote,
            Alignment,
            SourceEditing,
            Table,
            TableToolbar,
            TableProperties,
            TableCellProperties,
        ];

        if (allowImageUpload) {
            toolbarItems.push("imageUpload", "imageStyle:side");
            plugins.push(
                Image,
                ImageToolbar,
                ImageCaption,
                ImageStyle,
                ImageResize,
                ImageUpload,
                Base64UploadAdapter
            );
        }

        setEditorConfig({
            toolbar: { items: toolbarItems },
            plugins,
            image: allowImageUpload
                ? {
                    toolbar: [
                        "imageTextAlternative",
                        "imageStyle:inline",
                        "imageStyle:block",
                        "imageStyle:side",
                        "resizeImage",
                    ],
                }
                : undefined,
            fontSize: { options: ["tiny", "small", "default", "big", "huge"] },
            fontFamily: {
                options: [
                    "default",
                    "Arial, Helvetica, sans-serif",
                    "Courier New, Courier, monospace",
                    "Georgia, serif",
                    "Lucida Sans Unicode, Lucida Grande, sans-serif",
                    "Tahoma, Geneva, sans-serif",
                    "Times New Roman, Times, serif",
                    "Trebuchet MS, Helvetica, sans-serif",
                    "Verdana, Geneva, sans-serif",
                ],
            },
            table: {
                contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
            },
            ...config,
        });

        setLoading(false);
    }, [config, allowImageUpload]);

    const handleEditorChange = useCallback(
        (event: EventInfo, editor: ClassicEditor) => {
            const data = editor.getData();
            onChange?.(data);
        },
        [onChange]
    );

    return (
        <div>
            {loading ? (
                <div>Loading...</div>
            ) : (
                editorConfig && (
                    <CKEditor
                        key={JSON.stringify(editorConfig)}
                        editor={ClassicEditor}
                        data={initialData}
                        onChange={handleEditorChange}
                        config={editorConfig}
                        data-testid="rick-text-editor"
                        onReady={(editor) => {
                            const editable = editor.ui.getEditableElement();
                            if (editable) {
                                editable.style.height = "350px";
                                editable.style.overflowY = "auto";
                            }
                        }}
                    />
                )
            )}

            {isInvalid && helperText && <div>{helperText}</div>}
        </div>
    );
};

export default RichTextEditor;
